import type {
  StorefrontImageShape,
  StorefrontSizeToken,
} from "./appearance";

/**
 * MENU LAYOUT METRICS
 *
 * Kept out of `appearance.ts` on purpose: that file is a byte-for-byte PORTAL
 * mirror, and this file is how the STOREFRONT draws the contract. Mixing them
 * would make the mirror `diff` fail forever.
 *
 * In every table, `medium` is what the storefront already uses today — so
 * "never chose anything" and "the old storefront" stay the same screen.
 */

/**
 * Desktop menu columns. `medium` is today's `lg:grid-cols-2`.
 *
 * On mobile it is always one column (`grid-cols-1`); size only affects
 * {@link MENU_CARD_MIN_HEIGHT_REM}.
 */
export const MENU_CARD_COLUMNS: Record<StorefrontSizeToken, number> = {
  compact: 3,
  medium: 2,
  large: 1,
};

/**
 * Same columns as Tailwind class literals.
 *
 * Duplicates {@link MENU_CARD_COLUMNS} for tooling, not style: Tailwind scans
 * SOURCE for literal classes, so a runtime `` `lg:grid-cols-${n}` `` would never
 * land in the CSS. Written here next to the table they mirror, the scanner
 * finds them — and a test locks both tables in sync.
 */
export const MENU_CARD_COLUMN_CLASS: Record<StorefrontSizeToken, string> = {
  compact: "lg:grid-cols-3",
  medium: "lg:grid-cols-2",
  large: "lg:grid-cols-1",
};

/**
 * Menu card height FLOOR in `rem`.
 *
 * Without it, compact and large look almost identical when the photo is small —
 * the card shrinks to content and the merchant's size choice disappears.
 */
export const MENU_CARD_MIN_HEIGHT_REM: Record<StorefrontSizeToken, number> = {
  compact: 7,
  medium: 9.5,
  large: 13,
};

/**
 * Item photo side length in `rem`. `medium` is today's `h-32 w-32`.
 *
 * Steps come from `itemImage.size` ("Photo size"), NOT `cardSize` — how many
 * items fit vs how heavy the photo is inside the card. Tied together, "compact
 * card with large photo" was impossible. {@link MENU_CARD_MIN_HEIGHT_REM} is a
 * FLOOR, not a ceiling: the card may grow when the photo is a step larger.
 */
export const MENU_IMAGE_SIZE_REM: Record<StorefrontSizeToken, number> = {
  compact: 5.5,
  medium: 8,
  large: 11,
};

/** Photo aspect ratio (width ÷ height) by shape. */
export const MENU_IMAGE_ASPECT: Record<StorefrontImageShape, number> = {
  square: 1,
  round: 1,
  wide: 16 / 9,
};

/**
 * Only `wide` changes the card AXIS: photo on top at 16:9, text below.
 * The other shapes are side thumbnails and differ only in rounding.
 */
export function isStackedShape(shape: StorefrontImageShape): boolean {
  return shape === "wide";
}

/**
 * Desktop CATEGORY LIST columns. `medium` is the existing `md:grid-cols-2`.
 *
 * Uses `md:` not `lg:` (unlike the product menu): category cards are wide
 * photos with the name on top and already read well in two columns on tablet.
 * Mobile stays one column; size only drives {@link CATEGORY_CARD_HEIGHT_REM}.
 */
export const CATEGORY_CARD_COLUMNS: Record<StorefrontSizeToken, number> = {
  compact: 3,
  medium: 2,
  large: 1,
};

/** Same columns as Tailwind classes. See {@link MENU_CARD_COLUMN_CLASS}. */
export const CATEGORY_CARD_COLUMN_CLASS: Record<StorefrontSizeToken, string> = {
  compact: "md:grid-cols-3",
  medium: "md:grid-cols-2",
  large: "md:grid-cols-1",
};

/**
 * Category card height in `rem`. `medium` is today's `h-52` (13rem).
 *
 * FIXED height, not a floor: the photo IS the card (`object-cover`), so height
 * decides how much of the photo shows.
 */
export const CATEGORY_CARD_HEIGHT_REM: Record<StorefrontSizeToken, number> = {
  compact: 9,
  medium: 13,
  large: 17,
};
